import '../scss/components/_ownerreports.scss'
import React, { useState, useEffect, useMemo } from 'react'

// ─── Constants ───────────────────────────────────────────────────────────────

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const fmt = n => `₹${Math.round(n || 0).toLocaleString('en-IN')}`

// ─── Shared tile ─────────────────────────────────────────────────────────────

function StatTile({ label, value, sub, accent }) {
  return (
    <div className="rpt-stat-tile" style={{ borderTopColor: accent }}>
      <span className="rpt-stat-value" style={{ color: accent }}>{value}</span>
      <span className="rpt-stat-label">{label}</span>
      {sub && <span className="rpt-stat-sub">{sub}</span>}
    </div>
  )
}

// ─── Delay Tab ──────────────────────────────────────────────────────────────

function DelayTab({ invoiceData, selectedYear }) {
  const [statusF, setStatusF] = useState('all')
  const [sortCol, setSortCol] = useState('BalanceAmount')
  const [sortDir, setSortDir] = useState('desc')

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortCol(col)
      setSortDir('desc')
    }
  }

  const getStatusBadge = (status) => {
    const statusMap = {
      'PAID': { class: 'cp-rpt-st-res', color: '#22C55E', label: 'Paid' },
      'OVERDUE': { class: 'cp-rpt-st-rej', color: '#EF4444', label: 'Overdue' },
      'NOT PAID': { class: 'cp-rpt-st-open', color: '#F59E0B', label: 'Not Paid' },
      'PARTIAL': { class: 'cp-rpt-st-open', color: '#F59E0B', label: 'Partial' },
    }
    const statusInfo = statusMap[status] || statusMap['NOT PAID']
    return (
      <span className={`cp-rpt-status ${statusInfo.class}`}>
        <span className="cp-rpt-dot" style={{ background: statusInfo.color }} />
        {statusInfo.label}
      </span>
    )
  }

  // Filter and sort data
  const filtered = invoiceData.filter(r => {
    if (statusF === 'all') return true
    if (statusF === 'overdue') return r.PaymentStatus === 'OVERDUE'
    if (statusF === 'notpaid') return r.PaymentStatus === 'NOT PAID'
    if (statusF === 'paid') return r.PaymentStatus === 'PAID'
    return true
  })

  const sorted = [...filtered].sort((a, b) => {
    let av = a[sortCol]
    let bv = b[sortCol]
    if (typeof av === 'string') av = av.toLowerCase()
    if (typeof bv === 'string') bv = bv.toLowerCase()
    if (av < bv) return sortDir === 'asc' ? -1 : 1
    if (av > bv) return sortDir === 'asc' ? 1 : -1
    return 0
  })

  // Calculate totals
  const totalOutstanding = invoiceData.reduce((sum, r) => sum + (r.BalanceAmount > 0 ? r.BalanceAmount : 0), 0)
  const overdueCount = invoiceData.filter(r => r.PaymentStatus === 'OVERDUE').length
  const notPaidCount = invoiceData.filter(r => r.PaymentStatus === 'NOT PAID').length
  const paidCount = invoiceData.filter(r => r.PaymentStatus === 'PAID').length

  return (
    <div className="cp-rpt-section">
      <div className="rpt-tiles">
        <StatTile label="Total Outstanding" value={fmt(totalOutstanding)} sub={`${overdueCount + notPaidCount} invoices pending`} accent="#EF4444" />
        <StatTile label="Overdue" value={overdueCount} sub="Payment overdue" accent="#991B1B" />
        <StatTile label="Not Paid" value={notPaidCount} sub="Awaiting payment" accent="#F59E0B" />
        <StatTile label="Paid" value={paidCount} sub="Completed payments" accent="#22C55E" />
      </div>

      <div className="cp-rpt-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="cp-rpt-tbl-head">
          <span className="cp-rpt-card-title" style={{ margin: 0 }}>Invoice Details - {selectedYear}</span>
          <div className="cp-rpt-filter-group">
            <button 
              className={`cp-rpt-fpill${statusF === 'all' ? ' on' : ''}`} 
              onClick={() => setStatusF('all')}
            >
              All ({invoiceData.length})
            </button>
            <button 
              className={`cp-rpt-fpill${statusF === 'overdue' ? ' on' : ''}`} 
              onClick={() => setStatusF('overdue')}
            >
              Overdue ({invoiceData.filter(r => r.PaymentStatus === 'OVERDUE').length})
            </button>
            <button 
              className={`cp-rpt-fpill${statusF === 'notpaid' ? ' on' : ''}`} 
              onClick={() => setStatusF('notpaid')}
            >
              Not Paid ({invoiceData.filter(r => r.PaymentStatus === 'NOT PAID').length})
            </button>
            <button 
              className={`cp-rpt-fpill${statusF === 'paid' ? ' on' : ''}`} 
              onClick={() => setStatusF('paid')}
            >
              Paid ({invoiceData.filter(r => r.PaymentStatus === 'PAID').length})
            </button>
          </div>
        </div>
        <div className="cp-rpt-tbl-scroll">
          <table className="cp-rpt-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('TenantName')}>Tenant Name</th>
                <th onClick={() => handleSort('RoomNumber')}>Room</th>
                <th onClick={() => handleSort('BedNumber')}>Bed</th>
                <th onClick={() => handleSort('InvoiceDate')}>Invoice Date</th>
                <th onClick={() => handleSort('DueDate')}>Due Date</th>
                <th onClick={() => handleSort('TotalAmount')}>Total Amount</th>
                <th onClick={() => handleSort('PaidAmount')}>Paid</th>
                <th onClick={() => handleSort('BalanceAmount')}>Balance</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan="9" className="cp-rpt-empty-row">
                    No invoices match the selected filter.
                  </td>
                </tr>
              ) : (
                sorted.map((r, i) => (
                  <tr key={i}>
                    <td className="cp-rpt-td-bold">{r.TenantName || '—'}</td>
                    <td>{r.RoomNumber}</td>
                    <td>{r.BedNumber}</td>
                    <td>{r.InvoiceDate ? new Date(r.InvoiceDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</td>
                    <td>{r.DueDate ? new Date(r.DueDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</td>
                    <td className="cp-rpt-td-mono">{fmt(r.TotalAmount)}</td>
                    <td className="cp-rpt-td-mono" style={{ color: '#166534' }}>{fmt(r.PaidAmount)}</td>
                    <td className="cp-rpt-td-mono" style={{ 
                      color: r.BalanceAmount > 0 ? '#991B1B' : r.BalanceAmount < 0 ? '#166534' : '#6B7280',
                      fontWeight: r.BalanceAmount !== 0 ? 600 : 400
                    }}>
                      {fmt(r.BalanceAmount)}
                    </td>
                    <td>{getStatusBadge(r.PaymentStatus)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="cp-rpt-tbl-foot">
          {sorted.length} invoice{sorted.length !== 1 ? 's' : ''} · {selectedYear}
        </div>
      </div>
    </div>
  )
}

// ─── Month Wise Revenue Accordion ──────────────────────────────────────────

function RevenueMonthAccordion({ invoiceData, selectedYear }) {
  const [expandedYears, setExpandedYears] = useState(() => ({
    [selectedYear]: true
  }))

  const [expandedMonths, setExpandedMonths] = useState(() => ({}))
  const [expandedInvoices, setExpandedInvoices] = useState(() => ({}))

  const toggleYear = (year) => {
    setExpandedYears(prev => ({
      ...prev,
      [year]: !prev[year]
    }))
  }

  const toggleMonth = (key) => {
    setExpandedMonths(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  // Group invoices by year and month
  const groupedData = useMemo(() => {
    const grouped = {}
    
    invoiceData.forEach(invoice => {
      const invoiceDate = invoice.InvoiceDate ? new Date(invoice.InvoiceDate) : null
      if (!invoiceDate) return
      
      const year = invoiceDate.getFullYear()
      const month = invoiceDate.toLocaleString('default', { month: 'long' })
      
      if (!grouped[year]) grouped[year] = {}
      if (!grouped[year][month]) grouped[year][month] = []
      
      grouped[year][month].push(invoice)
    })
    
    // Sort invoices in each month by RoomNumber
    Object.keys(grouped).forEach(year => {
      Object.keys(grouped[year]).forEach(month => {
        grouped[year][month].sort((a, b) => {
          const roomA = parseInt(a.RoomNumber) || 0
          const roomB = parseInt(b.RoomNumber) || 0
          return roomA - roomB
        })
      })
    })
    
    return grouped
  }, [invoiceData])

  const availableYears = Object.keys(groupedData)
    .map(Number)
    .sort((a, b) => b - a)

  // Calculate totals
  const getMonthTotal = (monthData) => {
    return monthData.reduce((sum, inv) => sum + inv.TotalAmount, 0)
  }

  const getMonthCollected = (monthData) => {
    return monthData.reduce((sum, inv) => sum + inv.PaidAmount, 0)
  }

  const getStatusBadge = (status) => {
    const statusMap = {
      'PAID': { class: 'cp-rpt-st-res', color: '#22C55E', label: 'Paid' },
      'OVERDUE': { class: 'cp-rpt-st-rej', color: '#EF4444', label: 'Overdue' },
      'NOT PAID': { class: 'cp-rpt-st-open', color: '#F59E0B', label: 'Not Paid' },
      'PARTIAL': { class: 'cp-rpt-st-open', color: '#F59E0B', label: 'Partial' },
    }
    const statusInfo = statusMap[status] || statusMap['NOT PAID']
    return (
      <span className={`cp-rpt-status ${statusInfo.class}`}>
        <span className="cp-rpt-dot" style={{ background: statusInfo.color }} />
        {statusInfo.label}
      </span>
    )
  }

  return (
    <div className="rpt-occupancy-accordion">
      <h3 className="rpt-accordion-title">Month Wise Revenue Details</h3>
      
      {availableYears.map(year => {
        const yearData = groupedData[year] || {}
        const isYearExpanded = expandedYears[year] || false
        const yearMonths = Object.keys(yearData)
        
        const totalYearRevenue = yearMonths.reduce(
          (sum, month) => sum + getMonthTotal(yearData[month]), 0
        )
        const totalYearCollected = yearMonths.reduce(
          (sum, month) => sum + getMonthCollected(yearData[month]), 0
        )

        return (
          <div key={year} className="rpt-year-accordion">
            <button 
              className="rpt-year-toggle"
              onClick={() => toggleYear(year)}
            >
              <span className="rpt-accordion-chevron">
                {isYearExpanded ? '▼' : '▶'}
              </span>
              <span className="rpt-year-label">{year}</span>
              <span className="rpt-year-count">
                (₹{totalYearRevenue.toLocaleString('en-IN')} billed · ₹{totalYearCollected.toLocaleString('en-IN')} collected)
              </span>
            </button>

            {isYearExpanded && (
              <div className="rpt-year-content">
                {yearMonths.map(month => {
                  const monthData = yearData[month] || []
                  const monthKey = `${year}-${month}`
                  const isMonthExpanded = expandedMonths[monthKey] || false
                  const monthTotal = getMonthTotal(monthData)
                  const monthCollected = getMonthCollected(monthData)

                  return (
                    <div key={month} className="rpt-month-accordion">
                      <button 
                        className="rpt-month-toggle"
                        onClick={() => toggleMonth(monthKey)}
                      >
                        <span className="rpt-accordion-chevron">
                          {isMonthExpanded ? '▼' : '▶'}
                        </span>
                        <span className="rpt-month-label">{month}</span>
                        <span className="rpt-month-count">
                          (₹{monthTotal.toLocaleString('en-IN')} billed · ₹{monthCollected.toLocaleString('en-IN')} collected)
                        </span>
                      </button>

                      {isMonthExpanded && (
                        <div className="rpt-month-content">
                          {monthData.length > 0 ? (
                            <table className="rpt-occupancy-table">
                              <thead>
                                <tr>
                                  <th>S.No</th>
                                  <th>Room No</th>
                                  <th>Bed Number</th>
                                  <th>Room Type</th>
                                  <th>Tenant Name</th>
                                  <th>Rent / Month</th>
                                  <th>Electricity Bill</th>
                                  <th>Total Amount</th>
                                  <th>Collected</th>
                                  <th>Payment Status</th>
                                  <th>Transaction ID</th>
                                </tr>
                              </thead>
                              <tbody>
                                {monthData.map((inv, index) => (
                                  <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{inv.RoomNumber || '—'}</td>
                                    <td>{inv.BedNumber || '—'}</td>
                                    <td>—</td>
                                    <td>{inv.TenantName || '—'}</td>
                                    <td className="cp-rpt-td-mono">{inv.RentAmount ? fmt(inv.RentAmount) : '—'}</td>
                                    <td className="cp-rpt-td-mono">{inv.ElectricityAmount !== undefined && inv.ElectricityAmount !== null ? fmt(inv.ElectricityAmount) : '—'}</td>
                                    <td className="cp-rpt-td-mono">{inv.TotalAmount ? fmt(inv.TotalAmount) : '—'}</td>
                                    <td className="cp-rpt-td-mono" style={{ color: '#166534' }}>{inv.PaidAmount ? fmt(inv.PaidAmount) : '—'}</td>
                                    <td>{getStatusBadge(inv.PaymentStatus)}</td>
                                    <td>{inv.TransactionID || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="cp-rpt-total-row">
                                  <td colSpan="5" className="cp-rpt-td-bold">Monthly Total</td>
                                  <td className="cp-rpt-td-mono">
                                    <strong>{fmt(monthData.reduce((sum, inv) => sum + (inv.RentAmount || 0), 0))}</strong>
                                  </td>
                                  <td className="cp-rpt-td-mono">
                                    <strong>{fmt(monthData.reduce((sum, inv) => sum + (inv.ElectricityAmount || 0), 0))}</strong>
                                  </td>
                                  <td className="cp-rpt-td-mono">
                                    <strong>{fmt(monthData.reduce((sum, inv) => sum + (inv.TotalAmount || 0), 0))}</strong>
                                  </td>
                                  <td className="cp-rpt-td-mono">
                                    <strong style={{ color: '#166534' }}>{fmt(monthCollected)}</strong>
                                  </td>
                                  <td colSpan="2"></td>
                                </tr>
                              </tfoot>
                            </table>
                          ) : (
                            <p className="rpt-empty-message">No invoices for this month</p>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── Revenue Tab ──────────────────────────────────────────────────────────────

function RevenueTab({ invoiceData, selectedYear, summaryData, outstandingData }) {
  // Calculate totals from invoice data
  const totalBilled = invoiceData.reduce((sum, inv) => sum + inv.TotalAmount, 0)
  const totalCollected = invoiceData.reduce((sum, inv) => sum + inv.PaidAmount, 0)
  const totalOutstanding = invoiceData.reduce((sum, inv) => sum + (inv.BalanceAmount > 0 ? inv.BalanceAmount : 0), 0)
  
  // Use summary data if available, otherwise calculate
  const collectionRate = totalBilled > 0 ? Math.round((totalCollected / totalBilled) * 100) : 0
  
  // Get overdue and not paid totals from outstanding data
  const overdueTotal = outstandingData?.OverDue || 0
  const notPaidTotal = outstandingData?.NotPaid || 0

  // Monthly collection chart data
  const monthlyData = useMemo(() => {
    const totals = {}
    invoiceData.forEach(inv => {
      const date = inv.InvoiceDate ? new Date(inv.InvoiceDate) : null
      if (!date) return
      const month = date.toLocaleString('default', { month: 'short' })
      const year = date.getFullYear()
      const key = `${month}-${year}`
      if (!totals[key]) totals[key] = 0
      totals[key] += inv.PaidAmount
    })
    return totals
  }, [invoiceData])

  const chartData = MONTHS.map(month => {
    const key = `${month}-${selectedYear}`
    return monthlyData[key] || 0
  })

  const maxVal = Math.max(...chartData, 1) * 1.05

  return (
    <div className="rpt-section">
      <div className="rpt-tiles">
        <StatTile 
          label="Total Billed" 
          value={fmt(totalBilled)} 
          sub={`${collectionRate}% collection rate`} 
          accent="#6366F1" 
        />
        <StatTile 
          label="Collected" 
          value={fmt(totalCollected)} 
          sub={`${collectionRate}% of total billed`} 
          accent="#22C55E" 
        />
        <StatTile 
          label="Outstanding" 
          value={fmt(totalOutstanding)} 
          sub={`${invoiceData.filter(inv => inv.BalanceAmount > 0).length} invoices pending`} 
          accent="#EF4444" 
        />
        <StatTile 
          label="Overdue" 
          value={fmt(overdueTotal)} 
          sub="Payment overdue" 
          accent="#991B1B" 
        />
      </div>

      <div className="rpt-chart-grid">
        <div className="rpt-chart-card">
          <div className="rpt-chart-title">
            <span>Monthly Collection • {selectedYear}</span>
            <span className="sub">
              Total: {fmt(totalCollected)}
            </span>
          </div>
          <div className="rpt-legend">
            <span><span className="rpt-legend-dot met"></span> Collected</span>
            <span><span className="rpt-legend-dot future"></span> No data yet</span>
          </div>
          <div className="rpt-barchart">
            {chartData.map((val, i) => {
              const hasData = val > 0
              const isFuture = !hasData && i > new Date().getMonth()
              return (
                <div key={i} className="rpt-bar-col">
                  <span className="rpt-bar-value">{hasData ? fmt(val) : '—'}</span>
                  <div
                    className={`rpt-bar ${hasData ? 'rpt-bar-met' : isFuture ? 'rpt-bar-future' : 'rpt-bar-future'}`}
                    style={{ height: hasData ? `${Math.max(4, (val / maxVal) * 100)}%` : '4px' }}
                  />
                  <span className="rpt-bar-label">{MONTHS[i]}</span>
                </div>
              )
            })}
          </div>
          <div className="rpt-target-section">
            <div className="rpt-target-label">Total collected</div>
            <div className="rpt-target-meta">
              <strong>{fmt(totalCollected)}</strong>
              <span style={{ color: '#6B7280', marginLeft: 6 }}>
                ({chartData.filter(v => v > 0).length} months)
              </span>
            </div>
          </div>
        </div>

        <div className="rpt-quarter-card rpt-card-accent-left" style={{ borderLeftColor: '#EF4444' }}>
          <div className="rpt-quarter-label">Outstanding Breakdown</div>
          <div className="rpt-quarter-value" style={{ color: '#EF4444' }}>
            {fmt(totalOutstanding)}
          </div>
          <div className="rpt-quarter-sub" style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px' }}>
            <span>Overdue: <strong>{fmt(overdueTotal)}</strong></span>
            <span>Not Paid: <strong>{fmt(notPaidTotal)}</strong></span>
          </div>
        </div>
      </div>

      <RevenueMonthAccordion 
        invoiceData={invoiceData}
        selectedYear={selectedYear}
      />
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// ROOT
// ═══════════════════════════════════════════════════════════════════════════════

const OwnerRevenueReport = ({ selectedPGId, selectedYear, dateFrom, dateTo }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [tab, setTab] = useState('revenue')
  
  // API response data
  const [invoiceData, setInvoiceData] = useState([]) // First array - invoice details
  const [monthlySummary, setMonthlySummary] = useState([]) // Second array - monthly summary
  const [overallSummary, setOverallSummary] = useState(null) // Third array - overall summary
  const [outstandingData, setOutstandingData] = useState(null) // Fourth array - outstanding breakdown
  const [occupancyData, setOccupancyData] = useState(null) // Fifth array - occupancy stats

  useEffect(() => {
    if (!selectedPGId || !dateFrom || !dateTo) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    import('../apis/services/pgApi').then(({ getRevenueReport }) => {
      getRevenueReport(selectedPGId, dateFrom, dateTo)
        .then(response => {
          // Response is an array with 5 parts: [invoices, monthlySummary, overallSummary, outstandingData, occupancyData]
          if (Array.isArray(response) && response.length >= 5) {
            setInvoiceData(response[0] || [])
            setMonthlySummary(response[1] || [])
            setOverallSummary(response[2]?.[0] || null)
            setOutstandingData(response[3]?.[0] || null)
            setOccupancyData(response[4]?.[0] || null)
          } else if (response?.data && Array.isArray(response.data)) {
            const data = response.data
            setInvoiceData(data[0] || [])
            setMonthlySummary(data[1] || [])
            setOverallSummary(data[2]?.[0] || null)
            setOutstandingData(data[3]?.[0] || null)
            setOccupancyData(data[4]?.[0] || null)
          } else {
            setError('Unexpected API response format')
          }
        })
        .catch(err => {
          console.error('Error fetching revenue report:', err)
          setError(err.message || 'Failed to load revenue data')
        })
        .finally(() => setLoading(false))
    }).catch(err => {
      console.error('Error importing API:', err)
      setError('Failed to load API module')
      setLoading(false)
    })
  }, [selectedPGId, dateFrom, dateTo])

  if (loading) return <div className="rpt-state">Loading revenue data…</div>
  if (error) return <div className="rpt-state rpt-state-error">Error: {error}</div>

  if (invoiceData.length === 0) {
    return <div className="rpt-state">No revenue data available for the selected period</div>
  }

  return (
    <div className="cp-rpt-root">
      <div className="cp-rpt-view-tabs">
        {[
          { key: 'revenue', label: 'Revenue' },
          { key: 'delay',   label: 'Delay'   },
        ].map(t => (
          <button
            key={t.key}
            className={`cp-rpt-vtab${tab === t.key ? ' on' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'revenue' && (
        <RevenueTab 
          invoiceData={invoiceData}
          selectedYear={selectedYear}
          summaryData={overallSummary}
          outstandingData={outstandingData}
        />
      )}
      {tab === 'delay' && (
        <DelayTab 
          invoiceData={invoiceData}
          selectedYear={selectedYear}
        />
      )}
    </div>
  )
}

export default OwnerRevenueReport